import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Plus, Trash2, Save, Calculator, Eye, GripVertical, Pencil, Copy, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatPriceFixed, round2 } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FIELD_TYPES = [
  { value: 'number', label: 'Number' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'calculated', label: 'Calculated (Formula)' },
  { value: 'boolean', label: 'Yes / No' },
];

const makeId = () => 'f_' + Math.random().toString(36).substr(2, 8);
const makeRoomId = () => 'rt_' + Math.random().toString(36).substr(2, 8);

const emptyField = () => ({
  field_id: makeId(), label: '', type: 'number', options: [], formula: '',
  has_helper: false, order: 0, required: false, client_visible: true, unit: '', group: '', remark: '', enable_remarks: false,
});

const emptyRoomType = () => ({
  room_type_id: makeRoomId(), name: '', inputs: [{ key: 'width', label: 'Width (ft)', type: 'number' }],
  formula: '', order: 0, remark: '',
});

export default function QuotationBuilder() {
  const [templates, setTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const activeTemplateRef = useRef(null);
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [fields, setFields] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [fieldDialog, setFieldDialog] = useState({ open: false, field: null, index: -1 });
  const [roomDialog, setRoomDialog] = useState({ open: false, room: null, index: -1 });
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/quotation-templates`, { withCredentials: true });
      setTemplates(res.data);
      if (res.data.length > 0) {
        const currentId = activeTemplateRef.current?.template_id;
        const match = currentId && res.data.find(t => t.template_id === currentId);
        loadTemplate(match || res.data[0]);
      }
    } catch (e) { /* empty list */ }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // Mark dirty on edits
  const trackName = (v) => { setName(v); setDirty(true); };
  const trackIsActive = (v) => { setIsActive(v); setDirty(true); };
  const trackFields = (v) => { setFields(v); setDirty(true); };
  const trackRoomTypes = (v) => { setRoomTypes(v); setDirty(true); };

  const loadTemplate = (t) => {
    setActiveTemplate(t);
    activeTemplateRef.current = t;
    setName(t.name);
    setIsActive(t.is_active);
    setFields(t.fields || []);
    setRoomTypes(t.room_types || []);
    setDirty(false);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Template name required'); return; }
    setSaving(true);
    const payload = { name, fields, room_types: roomTypes, is_active: isActive };
    try {
      if (activeTemplate) {
        await axios.put(`${BACKEND_URL}/api/quotation-templates/${activeTemplate.template_id}`, payload, { withCredentials: true });
        toast.success('Template updated');
      } else {
        const res = await axios.post(`${BACKEND_URL}/api/quotation-templates`, payload, { withCredentials: true });
        setActiveTemplate(res.data);
        toast.success('Template created');
      }
      setDirty(false);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleNewTemplate = () => {
    setActiveTemplate(null);
    setName('');
    setIsActive(true);
    setFields([]);
    setRoomTypes([]);
  };

  // ---- Field CRUD ----
  const openFieldDialog = (field, index) => setFieldDialog({ open: true, field: { ...field, options: field.options?.map(o => ({ ...o })) || [] }, index });
  const addField = () => {
    const f = emptyField();
    f.order = fields.length;
    openFieldDialog(f, -1);
  };
  const saveFieldDialog = () => {
    const f = fieldDialog.field;
    if (!f.label.trim()) { toast.error('Field label required'); return; }
    // Warn for dropdown issues
    if (f.type === 'dropdown') {
      if (!f.options || f.options.length === 0) {
        toast.warning('Dropdown has no options — it will show empty to customers.');
      } else {
        const emptyLabels = f.options.filter(o => !o.label?.trim());
        const emptyValues = f.options.filter(o => o.value === null || o.value === undefined || o.value === '');
        if (emptyLabels.length > 0) {
          toast.warning(`${emptyLabels.length} option(s) missing a label — they won't appear in the dropdown.`);
        }
        if (emptyValues.length > 0) {
          toast.warning(`${emptyValues.length} option(s) missing a numeric value — formulas will treat them as zero.`);
        }
      }
    }
    // Warn for calculated field with empty formula
    if (f.type === 'calculated' && !f.formula?.trim()) {
      toast.warning('Calculated field has no formula — it will always show zero.');
    }
    const updated = [...fields];
    if (fieldDialog.index >= 0) {
      const oldLabel = fields[fieldDialog.index].label;
      updated[fieldDialog.index] = f;
      // Auto-update formulas if label was renamed
      if (oldLabel && oldLabel !== f.label) {
        const oldRef = `{${oldLabel}}`;
        const newRef = `{${f.label}}`;
        for (let i = 0; i < updated.length; i++) {
          if (updated[i].formula && updated[i].formula.includes(oldRef)) {
            updated[i] = { ...updated[i], formula: updated[i].formula.split(oldRef).join(newRef) };
          }
        }
        toast.info(`Formulas updated: "${oldLabel}" → "${f.label}"`);
      }
    } else {
      updated.push(f);
    }
    trackFields(updated);
    setFieldDialog({ open: false, field: null, index: -1 });
  };
  const removeField = (idx) => trackFields(fields.filter((_, i) => i !== idx));
  const handleFieldDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = fields.findIndex(f => f.field_id === active.id);
    const newIdx = fields.findIndex(f => f.field_id === over.id);
    const reordered = arrayMove(fields, oldIdx, newIdx);
    reordered.forEach((f, i) => { f.order = i; });
    trackFields(reordered);
  };

  // ---- Room Type CRUD ----
  const openRoomDialog = (room, index) => setRoomDialog({ open: true, room: { ...room, inputs: room.inputs?.map(i => ({ ...i })) || [] }, index });
  const addRoomType = () => {
    const r = emptyRoomType();
    r.order = roomTypes.length;
    openRoomDialog(r, -1);
  };
  const saveRoomDialog = () => {
    const r = roomDialog.room;
    if (!r.name.trim()) { toast.error('Room type name required'); return; }
    const updated = [...roomTypes];
    if (roomDialog.index >= 0) {
      updated[roomDialog.index] = r;
    } else {
      updated.push(r);
    }
    trackRoomTypes(updated);
    setRoomDialog({ open: false, room: null, index: -1 });
  };
  const removeRoomType = (idx) => trackRoomTypes(roomTypes.filter((_, i) => i !== idx));
  const handleRoomDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = roomTypes.findIndex(r => r.room_type_id === active.id);
    const newIdx = roomTypes.findIndex(r => r.room_type_id === over.id);
    const reordered = arrayMove(roomTypes, oldIdx, newIdx);
    reordered.forEach((r, i) => { r.order = i; });
    trackRoomTypes(reordered);
  };

  // ---- Option helpers (inside field dialog) ----
  const updateDialogField = (key, val) => setFieldDialog(p => ({ ...p, field: { ...p.field, [key]: val } }));
  const addOption = () => updateDialogField('options', [...(fieldDialog.field?.options || []), { label: '', value: null }]);
  const removeOption = (idx) => updateDialogField('options', fieldDialog.field.options.filter((_, i) => i !== idx));
  const updateOption = (idx, key, val) => {
    const opts = [...fieldDialog.field.options];
    opts[idx] = { ...opts[idx], [key]: val };
    updateDialogField('options', opts);
  };

  // ---- Room input helpers (inside room dialog) ----
  const updateDialogRoom = (key, val) => setRoomDialog(p => ({ ...p, room: { ...p.room, [key]: val } }));
  const addRoomInput = () => updateDialogRoom('inputs', [...(roomDialog.room?.inputs || []), { key: '', label: '', type: 'number' }]);
  const removeRoomInput = (idx) => updateDialogRoom('inputs', roomDialog.room.inputs.filter((_, i) => i !== idx));
  const updateRoomInput = (idx, key, val) => {
    const inputs = [...roomDialog.room.inputs];
    inputs[idx] = { ...inputs[idx], [key]: val };
    updateDialogRoom('inputs', inputs);
  };

  const fieldLabels = fields.map(f => f.label).filter(Boolean);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const typeBadge = (type) => {
    const colors = { number: 'bg-blue-100 text-blue-800', dropdown: 'bg-violet-100 text-violet-800', calculated: 'bg-amber-100 text-amber-800', boolean: 'bg-teal-100 text-teal-800' };
    return <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${colors[type] || 'bg-stone-100 text-stone-600'}`}>{type}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Quotation Builder</h1>
          <p className="text-xs text-stone-500 mt-1">Configure the quotation template your clients will use</p>
        </div>
        <div className="flex gap-2">
          {templates.length > 0 && (
            <Button data-testid="new-template-btn" variant="outline" size="sm" onClick={handleNewTemplate} className="text-xs h-8 border-stone-200">
              <Plus className="w-3 h-3 mr-1" /> New Template
            </Button>
          )}
          <Button data-testid="preview-template-btn" variant="outline" size="sm" onClick={() => setPreviewOpen(true)} className="text-xs h-8 border-stone-200" disabled={fields.length === 0}>
            <Eye className="w-3 h-3 mr-1" /> Test Formulas
          </Button>
          <Button data-testid="save-template-btn" size="sm" onClick={handleSave} disabled={saving} className={`text-xs h-8 ${dirty ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-amber-700 hover:bg-amber-800'} text-white`}>
            <Save className="w-3 h-3 mr-1" /> {saving ? 'Saving...' : dirty ? 'Unsaved Changes' : activeTemplate ? 'Update Template' : 'Save Template'}
          </Button>
        </div>
      </div>

      {/* Template selector if multiple */}
      {templates.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {templates.map(t => (
            <button key={t.template_id} onClick={() => loadTemplate(t)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${activeTemplate?.template_id === t.template_id ? 'bg-amber-700 text-white border-amber-700' : 'bg-white border-stone-200 text-stone-700 hover:bg-stone-50'}`}>
              {t.name} {t.is_active && <span className="ml-1 text-[10px] opacity-75">(Active)</span>}
            </button>
          ))}
        </div>
      )}

      {/* Template Info */}
      <div className="bg-white border border-stone-200 rounded-lg p-4 flex gap-4 items-end">
        <div className="flex-1">
          <Label className="text-xs font-medium text-stone-600">Template Name</Label>
          <Input data-testid="template-name-input" value={name} onChange={e => trackName(e.target.value)} placeholder="e.g., Interior Work Quotation" className="border-stone-200 mt-1" />
        </div>
        <div className="flex items-center gap-2 pb-1">
          <Switch data-testid="template-active-toggle" checked={isActive} onCheckedChange={trackIsActive} />
          <Label className="text-xs text-stone-600">Active (Public)</Label>
        </div>
      </div>

      {/* FIELDS Section */}
      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-stone-200 bg-stone-50 flex justify-between items-center">
          <h3 className="text-xs font-semibold text-stone-700">Quotation Fields ({fields.length})</h3>
          <Button data-testid="add-field-btn" size="sm" variant="outline" onClick={addField} className="text-xs h-7 border-stone-200">
            <Plus className="w-3 h-3 mr-1" /> Add Field
          </Button>
        </div>
        {fields.length === 0 ? (
          <div className="p-8 text-center text-stone-400 text-xs">No fields yet. Click "Add Field" to start building your quotation template.</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFieldDragEnd}>
            <SortableContext items={fields.map(f => f.field_id)} strategy={verticalListSortingStrategy}>
              <div className="divide-y divide-stone-100">
                {fields.map((f, idx) => (
                  <SortableFieldRow key={f.field_id} field={f} idx={idx} typeBadge={typeBadge}
                    onEdit={() => openFieldDialog(f, idx)} onDelete={() => removeField(idx)} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* ROOM TYPES Section */}
      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-stone-200 bg-stone-50 flex justify-between items-center">
          <div>
            <h3 className="text-xs font-semibold text-stone-700">Room Calculator Types ({roomTypes.length})</h3>
            <p className="text-[10px] text-stone-400 mt-0.5">Items clients can add in the plywood sheet calculator</p>
          </div>
          <Button data-testid="add-room-type-btn" size="sm" variant="outline" onClick={addRoomType} className="text-xs h-7 border-stone-200">
            <Plus className="w-3 h-3 mr-1" /> Add Room Type
          </Button>
        </div>
        {roomTypes.length === 0 ? (
          <div className="p-8 text-center text-stone-400 text-xs">No room types yet. Add room types for the plywood calculator helper.</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRoomDragEnd}>
            <SortableContext items={roomTypes.map(r => r.room_type_id)} strategy={verticalListSortingStrategy}>
              <div className="divide-y divide-stone-100">
                {roomTypes.map((r, idx) => (
                  <SortableRoomRow key={r.room_type_id} room={r} idx={idx}
                    onEdit={() => openRoomDialog(r, idx)} onDelete={() => removeRoomType(idx)} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* FIELD EDIT DIALOG */}
      <Dialog open={fieldDialog.open} onOpenChange={(v) => !v && setFieldDialog({ open: false, field: null, index: -1 })}>
        <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{fieldDialog.index >= 0 ? 'Edit Field' : 'Add Field'}</DialogTitle></DialogHeader>
          {fieldDialog.field && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Field Label *</Label>
                <Input data-testid="field-label-input" value={fieldDialog.field.label} onChange={e => updateDialogField('label', e.target.value)} placeholder="e.g., Plywood Sheets Estimated" className="border-stone-200 mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={fieldDialog.field.type} onValueChange={v => updateDialogField('type', v)}>
                    <SelectTrigger data-testid="field-type-select" className="border-stone-200 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Unit (optional)</Label>
                  <Input value={fieldDialog.field.unit} onChange={e => updateDialogField('unit', e.target.value)} placeholder="sheets, nos, sq.ft" className="border-stone-200 mt-1" />
                </div>
              </div>

              <div>
                <Label className="text-xs">Group Name (optional)</Label>
                <p className="text-[10px] text-stone-400 mb-1">Fields with the same group name appear together in one row for the client. E.g., "Tandem Boxes"</p>
                <Input data-testid="field-group-input" value={fieldDialog.field.group || ''} onChange={e => updateDialogField('group', e.target.value)} placeholder="e.g., Tandem Boxes" className="border-stone-200" />
              </div>

              <div>
                <Label className="text-xs">Remark / Hint (optional)</Label>
                <p className="text-[10px] text-stone-400 mb-1">Shown below the label as hint text, and as tooltip on info icon. Guides the client.</p>
                <Input data-testid="field-remark-input" value={fieldDialog.field.remark || ''} onChange={e => updateDialogField('remark', e.target.value)} placeholder="e.g., Higher quality = longer warranty" className="border-stone-200" />
              </div>

              {fieldDialog.field.type === 'dropdown' && (
                <div>
                  <Label className="text-xs">Options</Label>
                  <p className="text-[10px] text-stone-400 mb-2">Add options. Numeric value is used when this field is referenced in formulas.</p>
                  {(!fieldDialog.field.options || fieldDialog.field.options.length === 0) && (
                    <div className="mb-2 px-2 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-[10px] text-amber-800 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0" /> No options added — dropdown will be empty for customers.
                    </div>
                  )}
                  <div className="space-y-2">
                    {fieldDialog.field.options?.map((opt, oi) => (
                      <div key={oi} className="flex gap-2 items-center">
                        <Input value={opt.label} onChange={e => updateOption(oi, 'label', e.target.value)} placeholder="Label" className={`flex-1 text-xs ${!opt.label?.trim() ? 'border-amber-400 bg-amber-50/50' : 'border-stone-200'}`} />
                        <Input type="number" value={opt.value ?? ''} onChange={e => updateOption(oi, 'value', e.target.value ? parseFloat(e.target.value) : null)} placeholder="Value" className={`w-24 text-xs ${opt.value === null || opt.value === undefined || opt.value === '' ? 'border-amber-400 bg-amber-50/50' : 'border-stone-200'}`} />
                        <button onClick={() => removeOption(oi)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                    {fieldDialog.field.options?.length > 0 && fieldDialog.field.options.some(o => o.value === null || o.value === undefined || o.value === '') && (
                      <div className="px-2 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-[10px] text-amber-800 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 flex-shrink-0" /> Options without a numeric value will be treated as zero in formulas.
                      </div>
                    )}
                    <Button type="button" variant="outline" size="sm" onClick={addOption} className="text-xs h-7 w-full border-dashed border-stone-300">
                      <Plus className="w-3 h-3 mr-1" /> Add Option
                    </Button>
                  </div>
                </div>
              )}

              {fieldDialog.field.type === 'calculated' && (
                <div>
                  <Label className="text-xs">Formula</Label>
                  <p className="text-[10px] text-stone-400 mb-1">Reference other fields by name in curly braces. Use <code className="bg-amber-100 text-amber-800 px-1 rounded font-bold">{'{SUM_ALL}'}</code> to auto-sum all hidden calculated fields.</p>
                  <Input data-testid="field-formula-input" value={fieldDialog.field.formula} onChange={e => updateDialogField('formula', e.target.value)} placeholder="{SUM_ALL} or {Field A} * {Field B}" className="border-stone-200 font-mono text-xs" />
                  <div className="mt-2 flex flex-wrap gap-1">
                    <button type="button" onClick={() => updateDialogField('formula', (fieldDialog.field.formula || '') + '{SUM_ALL}')}
                      className="px-2 py-0.5 bg-amber-100 hover:bg-amber-200 rounded text-[10px] text-amber-800 font-mono font-bold transition-colors border border-amber-200">
                      <Copy className="w-2.5 h-2.5 inline mr-0.5" />{'{SUM_ALL}'}
                    </button>
                    {fieldLabels.filter(l => l !== fieldDialog.field.label).map(l => (
                      <button key={l} type="button" onClick={() => updateDialogField('formula', (fieldDialog.field.formula || '') + `{${l}}`)}
                        className="px-1.5 py-0.5 bg-stone-100 hover:bg-stone-200 rounded text-[10px] text-stone-700 font-mono transition-colors">
                        <Copy className="w-2.5 h-2.5 inline mr-0.5" />{`{${l}}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  <Switch checked={fieldDialog.field.required} onCheckedChange={v => updateDialogField('required', v)} />
                  <Label className="text-xs">Required</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={fieldDialog.field.client_visible} onCheckedChange={v => updateDialogField('client_visible', v)} />
                  <Label className="text-xs">Visible to Client</Label>
                </div>
                {fieldDialog.field.type === 'number' && (
                  <div className="flex items-center gap-2">
                    <Switch checked={fieldDialog.field.has_helper} onCheckedChange={v => updateDialogField('has_helper', v)} />
                    <Label className="text-xs flex items-center gap-1"><Calculator className="w-3 h-3" /> Room Calculator</Label>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Switch data-testid="field-enable-remarks-switch" checked={fieldDialog.field.enable_remarks} onCheckedChange={v => updateDialogField('enable_remarks', v)} />
                  <Label className="text-xs">Allow remarks (100 char) — visible on /get-quote and PDF</Label>
                </div>
              </div>

              <Button data-testid="save-field-btn" onClick={saveFieldDialog} className="w-full bg-amber-700 hover:bg-amber-800 text-white">
                {fieldDialog.index >= 0 ? 'Update Field' : 'Add Field'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ROOM TYPE EDIT DIALOG */}
      <Dialog open={roomDialog.open} onOpenChange={(v) => !v && setRoomDialog({ open: false, room: null, index: -1 })}>
        <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{roomDialog.index >= 0 ? 'Edit Room Type' : 'Add Room Type'}</DialogTitle></DialogHeader>
          {roomDialog.room && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Room / Item Name *</Label>
                <Input data-testid="room-name-input" value={roomDialog.room.name} onChange={e => updateDialogRoom('name', e.target.value)} placeholder="e.g., Kitchen Platform" className="border-stone-200 mt-1" />
              </div>
              <div>
                <Label className="text-xs">Client Inputs</Label>
                <p className="text-[10px] text-stone-400 mb-2">Define what the client enters for this room type. Key is used in the formula.</p>
                <div className="space-y-2">
                  {roomDialog.room.inputs?.map((inp, ii) => (
                    <div key={ii} className="flex gap-2 items-center">
                      <Input value={inp.key} onChange={e => updateRoomInput(ii, 'key', e.target.value.replace(/\s/g, '_').toLowerCase())} placeholder="key" className="w-24 border-stone-200 text-xs font-mono" />
                      <Input value={inp.label} onChange={e => updateRoomInput(ii, 'label', e.target.value)} placeholder="Display Label" className="flex-1 border-stone-200 text-xs" />
                      <Select value={inp.type} onValueChange={v => updateRoomInput(ii, 'type', v)}>
                        <SelectTrigger className="w-28 border-stone-200 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="dropdown">Dropdown</SelectItem>
                        </SelectContent>
                      </Select>
                      <button onClick={() => removeRoomInput(ii)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addRoomInput} className="text-xs h-7 w-full border-dashed border-stone-300">
                    <Plus className="w-3 h-3 mr-1" /> Add Input
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Formula (calculates plywood sheets contribution)</Label>
                <p className="text-[10px] text-stone-400 mb-1">Reference inputs by key. Example: <code className="bg-stone-100 px-1 rounded">{'{width} * {length} / 32'}</code></p>
                <Input data-testid="room-formula-input" value={roomDialog.room.formula} onChange={e => updateDialogRoom('formula', e.target.value)} placeholder="{width} * 2" className="border-stone-200 font-mono text-xs" />
                {roomDialog.room.inputs?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    <span className="text-[10px] text-stone-400 mr-1">Available:</span>
                    {roomDialog.room.inputs.filter(i => i.key).map(i => (
                      <button key={i.key} type="button" onClick={() => updateDialogRoom('formula', (roomDialog.room.formula || '') + `{${i.key}}`)}
                        className="px-1.5 py-0.5 bg-stone-100 hover:bg-stone-200 rounded text-[10px] text-stone-700 font-mono transition-colors">
                        {`{${i.key}}`}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Remark / Hint (optional)</Label>
                <Input data-testid="room-remark-input" value={roomDialog.room.remark || ''} onChange={e => updateDialogRoom('remark', e.target.value)} placeholder="e.g., Measure inner width only" className="border-stone-200" />
              </div>
              <Button data-testid="save-room-btn" onClick={saveRoomDialog} className="w-full bg-amber-700 hover:bg-amber-800 text-white">
                {roomDialog.index >= 0 ? 'Update Room Type' : 'Add Room Type'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* FORMULA DEBUGGER DIALOG */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Formula Debugger</DialogTitle></DialogHeader>
          <p className="text-xs text-stone-500 mb-4">Enter test values below to verify all calculations step by step. Hidden fields are shown with a dashed border.</p>
          <FormulaDebugger fields={fields} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Interactive Formula Debugger Component ---
function evalFormulaDebug(formula, values) {
  if (!formula) return 0;
  const expr = formula.replace(/\{([^}]+)\}/g, (_, name) => {
    const v = values[name];
    if (v === undefined || v === '' || v === null) return '0';
    const num = Number(v);
    return isNaN(num) ? '0' : String(num);
  });
  if (!/^[\d\s+\-*/().]+$/.test(expr.trim())) return 0;
  try { return new Function('return ' + expr.trim())(); } catch { return 0; }
}

function FormulaDebugger({ fields }) {
  const [testValues, setTestValues] = useState({});
  const sorted = [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Reset test values when fields change (new fields added, labels renamed, etc.)
  useEffect(() => {
    setTestValues({});
  }, [fields]);

  // Compute all calculated fields
  const labelMap = {};
  const computed = {};
  let hiddenCalcSum = 0;
  for (const f of sorted) {
    if (f.type === 'calculated' && f.formula) {
      const formulaWithSum = f.formula.replace(/\{SUM_ALL\}/gi, String(hiddenCalcSum));
      const val = evalFormulaDebug(formulaWithSum, labelMap);
      computed[f.field_id] = round2(val);
      labelMap[f.label] = computed[f.field_id];
      if (!f.client_visible) hiddenCalcSum += computed[f.field_id];
    } else if (f.type === 'dropdown') {
      const sel = testValues[f.field_id];
      const opt = f.options?.find(o => o.label === sel);
      labelMap[f.label] = opt?.value ?? 0;
    } else if (f.type === 'boolean') {
      labelMap[f.label] = testValues[f.field_id] ? 1 : 0;
    } else {
      labelMap[f.label] = parseFloat(testValues[f.field_id]) || 0;
    }
  }

  const fmt = formatPriceFixed;

  // Expand formula for display: replace {labels} with actual values
  const expandFormula = (formula) => {
    if (!formula) return '';
    return formula.replace(/\{SUM_ALL\}/gi, `[SUM_ALL = ${fmt(hiddenCalcSum)}]`).replace(/\{([^}]+)\}/g, (_, name) => {
      const v = labelMap[name];
      return v !== undefined ? String(round2(v)) : '0';
    });
  };

  return (
    <div className="space-y-1">
      {sorted.map(f => {
        const isCalc = f.type === 'calculated';
        const isHidden = !f.client_visible;
        const val = isCalc ? computed[f.field_id] : null;
        const isCurrency = f.unit === 'Rs' || f.unit === 'INR';

        return (
          <div key={f.field_id}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg ${isHidden ? 'border border-dashed border-stone-300 bg-stone-50/50' : 'bg-white border border-stone-200'}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium ${isHidden ? 'text-stone-500' : 'text-stone-800'}`}>{f.label}</span>
                {isHidden && <span className="px-1.5 py-0.5 rounded bg-stone-200 text-stone-500 text-[9px] font-semibold">HIDDEN</span>}
                {f.unit && <span className="text-[10px] text-stone-400">({f.unit})</span>}
              </div>
              {isCalc && f.formula && (
                <div className="mt-0.5 space-y-0.5">
                  <p className="text-[10px] text-stone-400 font-mono">{f.formula}</p>
                  <p className="text-[10px] text-blue-600 font-mono">= {expandFormula(f.formula)}</p>
                </div>
              )}
            </div>
            <div className="w-44 flex-shrink-0">
              {f.type === 'number' && (
                <Input
                  type="number" step="any" value={testValues[f.field_id] ?? ''}
                  onChange={e => setTestValues(p => ({ ...p, [f.field_id]: e.target.value }))}
                  placeholder="0" className="border-stone-200 text-xs text-right tabular-nums h-8"
                />
              )}
              {f.type === 'dropdown' && (
                <Select value={testValues[f.field_id] || ''} onValueChange={v => setTestValues(p => ({ ...p, [f.field_id]: v }))}>
                  <SelectTrigger className="border-stone-200 text-xs h-8"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{f.options?.map(o => <SelectItem key={o.label} value={o.label}>{o.label} {o.value != null && <span className="text-stone-400 ml-1">(={o.value})</span>}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {f.type === 'boolean' && (
                <div className="flex justify-end">
                  <Switch checked={!!testValues[f.field_id]} onCheckedChange={v => setTestValues(p => ({ ...p, [f.field_id]: v }))} />
                </div>
              )}
              {isCalc && (
                <div className={`text-right text-sm font-bold tabular-nums ${isHidden ? 'text-stone-600' : 'text-amber-800'}`}>
                  {isCurrency ? '\u20B9' : ''}{fmt(val ?? 0)}
                  {f.unit && !isCurrency && <span className="text-[10px] font-normal text-stone-400 ml-1">{f.unit}</span>}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// --- Sortable Field Row ---
function SortableFieldRow({ field: f, idx, typeBadge, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: f.field_id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined, position: 'relative' };

  return (
    <div ref={setNodeRef} style={style} data-testid={`field-row-${f.field_id}`}
      className={`flex items-center gap-3 px-4 py-2.5 hover:bg-stone-50/50 group ${isDragging ? 'bg-amber-50 shadow-lg rounded-lg border border-amber-200' : ''}`}>
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 touch-none">
        <GripVertical className="w-3.5 h-3.5 text-stone-400" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-stone-800 truncate">{f.label || '(untitled)'}</span>
          {typeBadge(f.type)}
          {f.has_helper && <span className="px-1.5 py-0.5 rounded bg-lime-100 text-lime-800 text-[10px] font-semibold flex items-center gap-0.5"><Calculator className="w-2.5 h-2.5" /> Helper</span>}
          {!f.client_visible && <span className="px-1.5 py-0.5 rounded bg-stone-200 text-stone-600 text-[10px] font-semibold">Admin Only</span>}
          {f.group && <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold">{f.group}</span>}
          {f.required && <span className="text-red-500 text-[10px]">*</span>}
        </div>
        {f.type === 'calculated' && f.formula && <p className="text-[10px] text-stone-400 mt-0.5 font-mono truncate">{f.formula}</p>}
        {f.type === 'dropdown' && f.options?.length > 0 && <p className="text-[10px] text-stone-400 mt-0.5">{f.options.map(o => o.label).join(', ')}</p>}
        {f.unit && <span className="text-[10px] text-stone-400">Unit: {f.unit}</span>}
        {f.remark && <p className="text-[10px] text-amber-600 mt-0.5 italic truncate">Hint: {f.remark}</p>}
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button data-testid={`edit-field-${idx}`} onClick={onEdit} className="p-1 hover:bg-stone-100 rounded"><Pencil className="w-3.5 h-3.5 text-stone-500" /></button>
        <button data-testid={`delete-field-${idx}`} onClick={onDelete} className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
      </div>
    </div>
  );
}

// --- Sortable Room Row ---
function SortableRoomRow({ room: r, idx, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: r.room_type_id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined, position: 'relative' };

  return (
    <div ref={setNodeRef} style={style} data-testid={`room-row-${r.room_type_id}`}
      className={`flex items-center gap-3 px-4 py-2.5 hover:bg-stone-50/50 group ${isDragging ? 'bg-amber-50 shadow-lg rounded-lg border border-amber-200' : ''}`}>
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 touch-none">
        <GripVertical className="w-3.5 h-3.5 text-stone-400" />
      </button>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-stone-800">{r.name || '(untitled)'}</span>
        <div className="flex gap-2 mt-0.5">
          <span className="text-[10px] text-stone-400">Inputs: {r.inputs?.map(i => i.label).join(', ') || 'none'}</span>
        </div>
        {r.formula && <p className="text-[10px] text-stone-400 font-mono truncate">Formula: {r.formula}</p>}
        {r.remark && <p className="text-[10px] text-amber-600 mt-0.5 italic truncate">Hint: {r.remark}</p>}
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button data-testid={`edit-room-${idx}`} onClick={onEdit} className="p-1 hover:bg-stone-100 rounded"><Pencil className="w-3.5 h-3.5 text-stone-500" /></button>
        <button onClick={onDelete} className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
      </div>
    </div>
  );
}
